import type { VenueOSClient } from '@mappedin/venueos-applet';
import type { AnalyticsClient } from '@venueos/analytics-sdk';
import {
  ArrowUpDown,
  ArrowUpRight,
  Building2,
  CalendarDays,
  Cctv,
  Check,
  DoorOpen,
  Globe as GlobeIcon,
  Monitor,
  PackageCheck,
  Plus,
  Radar,
  Radio,
  ShieldCheck,
  Sparkles,
  SquareDashedBottom,
  Thermometer,
  Video,
} from 'lucide-react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useMapEvents } from '../hooks/useMapEvents';
import { layerManager } from '../layers/layer-manager';
import type { MappedinConnection } from '../lib/mappedin-types';
import { isCameraChangeEvent } from '../lib/mappedin-types';
import { storage } from '../lib/storage';
import { cn } from '../lib/utils';
import { useAppStore } from '../state/app-store';

const ZT = 19.5;
const STORAGE_KEY = 'nav-toggles';

type ToggleKey =
  | 'cam'
  | 'guard'
  | 'dir'
  | 'events'
  | 'bluedot'
  | 'access'
  | 'temp'
  | 'assets'
  | 'vacancy'
  | 'elev';

type Toggles = Record<ToggleKey, boolean>;

const DEFAULTS: Toggles = {
  cam: true,
  guard: true,
  dir: false,
  events: false,
  bluedot: false,
  access: true,
  temp: true,
  assets: true,
  vacancy: false,
  elev: true,
};

const STACK_KEYS: Record<ToggleKey, boolean> = {
  cam: true,
  guard: true,
  dir: true,
  access: true,
  temp: true,
  assets: true,
  vacancy: true,
  elev: true,
  events: true,
  bluedot: true,
};

interface ChildItem {
  toggleKey: ToggleKey;
  label: string;
  active: boolean;
  toggle: (v: boolean) => void;
  icon: ReactNode;
  color: string;
  disabled?: boolean;
  dimmed?: boolean;
}

interface Section {
  slug: string;
  icon: ReactNode;
  name: string;
  color: string;
  active: boolean;
  isApp: boolean;
  disabled?: boolean;
  children: ChildItem[];
}

interface LayerLegendProps {
  client: VenueOSClient | null;
  analyticsClient: AnalyticsClient | null;
}

export function LayerLegend({
  client,
  analyticsClient,
}: LayerLegendProps): React.JSX.Element | null {
  const mapView = useAppStore((s) => s.mapView);
  const mapData = useAppStore((s) => s.mapData);
  const selectedVenue = useAppStore((s) => s.selectedVenue);
  const isStacked = useAppStore((s) => s.isStacked);
  const directoryKiosks = useAppStore((s) => s.directoryKiosks);
  const directoryKiosksLoading = useAppStore((s) => s.directoryKiosksLoading);
  const floorGap = useAppStore((s) => s.floorGap);

  const [toggles, setTogglesRaw] = useState<Toggles>(DEFAULTS);
  const loadedRef = useRef(false);
  const kioskSlugRef = useRef<string | null>(null);
  const popZonesSlugRef = useRef<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const raw = await storage.local.get(STORAGE_KEY);
        let saved: Partial<Toggles> | null = null;
        if (raw && typeof raw === 'string') {
          try {
            saved = JSON.parse(raw) as Partial<Toggles>;
          } catch {
            saved = null;
          }
        } else if (raw && typeof raw === 'object') {
          saved = raw as Partial<Toggles>;
        }
        if (saved) {
          const merged: Toggles = { ...DEFAULTS };
          for (const key of Object.keys(DEFAULTS) as ToggleKey[]) {
            const v = saved[key];
            if (v !== undefined) merged[key] = Boolean(v);
          }
          setTogglesRaw(merged);
        }
      } catch {
        // Use defaults on failure.
      }
      loadedRef.current = true;
    })();
  }, []);

  const setToggle = useCallback((key: ToggleKey, val: boolean): void => {
    setTogglesRaw((prev) => {
      const next: Toggles = { ...prev, [key]: val };
      void storage.local.set(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
    if (val && STACK_KEYS[key] && !useAppStore.getState().isStacked) {
      useAppStore.getState().setIsStacked(true);
      setTimeout(() => useAppStore.getState().applyStackedView(), 50);
    }
  }, []);

  useEffect(() => {
    if (!mapView) return undefined;
    document.documentElement.classList.add('markers-compact');
    const fn = (t: unknown): void => {
      if (!isCameraChangeEvent(t) || typeof t.zoomLevel !== 'number') return;
      if (t.zoomLevel >= ZT) document.documentElement.classList.remove('markers-compact');
      else document.documentElement.classList.add('markers-compact');
    };
    mapView.on('camera-change', fn);
    return () => {
      try {
        mapView.off('camera-change', fn);
      } catch {
        // Already detached.
      }
      document.documentElement.classList.remove('markers-compact');
    };
  }, [mapView]);

  const { events: mapEvents } = useMapEvents({
    client,
    venueSlug: selectedVenue?.slug ?? null,
  });

  // Kiosk fetch only depends on the host client, so dedupe on slug.
  useEffect(() => {
    if (!selectedVenue?.slug || !client) return;
    if (kioskSlugRef.current === selectedVenue.slug) return;
    kioskSlugRef.current = selectedVenue.slug;
    layerManager.loadDirectoryKiosks(client, selectedVenue.slug);
  }, [selectedVenue, client]);

  // Popular-zones (BlueDot) prefetch needs the typed AnalyticsClient,
  // which initializes one render after `client`. The previous version
  // shared a single `prevRef` with the kiosk fetch and stamped it on
  // the first run when `analyticsClient` was still null, so the prefetch
  // never re-fired once analytics finished initializing. Track the slug
  // separately and only stamp the ref after analytics is actually ready.
  useEffect(() => {
    if (!selectedVenue?.slug || !analyticsClient) return;
    if (popZonesSlugRef.current === selectedVenue.slug) return;
    popZonesSlugRef.current = selectedVenue.slug;
    layerManager.prefetchPopularZones(analyticsClient, selectedVenue.slug);
  }, [selectedVenue, analyticsClient]);

  useEffect(() => {
    if (!mapView || !mapData) return;
    if (toggles.cam && isStacked) layerManager.showCameraMarkers();
    else layerManager.clearCameras();
  }, [toggles.cam, mapView, mapData, isStacked]);

  useEffect(() => {
    if (!mapView || !mapData) return;
    if (toggles.dir && isStacked) layerManager.showDirectoryMarkers();
    else layerManager.clearDirectories();
  }, [toggles.dir, mapView, mapData, directoryKiosks, isStacked]);

  useEffect(() => {
    if (!mapView || !mapData) return undefined;
    if (!toggles.guard || !isStacked) {
      layerManager.clearGuards();
      return undefined;
    }
    // Wait for the stacked-view altitude tween to settle before placing
    // always-visible markers; adding them mid-tween causes Mappedin to
    // drop them silently. Same pattern as elevators below.
    const t = setTimeout(() => {
      layerManager.clearGuards();
      layerManager.showGuardMarkers();
    }, 2500);
    return () => clearTimeout(t);
  }, [toggles.guard, mapView, mapData, isStacked]);

  useEffect(() => {
    if (!mapView || !mapData) return;
    if (toggles.access && isStacked) layerManager.showAccessMarkers();
    else layerManager.clearAccess();
  }, [toggles.access, mapView, mapData, isStacked]);

  useEffect(() => {
    if (!mapView || !mapData) return;
    if (toggles.temp && isStacked) layerManager.showTempMarkers();
    else layerManager.clearTemps();
  }, [toggles.temp, mapView, mapData, isStacked]);

  useEffect(() => {
    if (!mapView || !mapData) return;
    if (toggles.assets && isStacked) layerManager.showAssetMarkers();
    else layerManager.clearAssets();
  }, [toggles.assets, mapView, mapData, isStacked]);

  // Track stack/gap so we can tell whether this effect run was caused by a
  // floor-altitude tween starting (initial auto-stack, manual stack toggle,
  // or a floor-gap change) or by a plain nav toggle. Mappedin silently drops
  // `always-visible` markers and floor-attached `Shapes.add` calls if they
  // land mid-tween, so we wait the tween out — but ONLY when a tween is
  // actually in flight. Toggling the elevator layer on/off shouldn't pay
  // that cost.
  const elevTweenRef = useRef({ isStacked, floorGap, mapData });
  useEffect(() => {
    if (!mapView || !mapData) return undefined;
    const prev = elevTweenRef.current;
    const tweenInFlight =
      prev.mapData !== mapData || prev.isStacked !== isStacked || prev.floorGap !== floorGap;
    elevTweenRef.current = { isStacked, floorGap, mapData };
    if (!toggles.elev || !isStacked) {
      layerManager.clearElevators();
      return undefined;
    }
    if (!tweenInFlight) {
      layerManager.clearElevators();
      layerManager.showElevators();
      return undefined;
    }
    const t = setTimeout(() => {
      layerManager.clearElevators();
      layerManager.showElevators();
    }, 2500);
    return () => clearTimeout(t);
  }, [toggles.elev, mapView, mapData, isStacked, floorGap]);

  useEffect(() => {
    if (!mapView || !mapData) return;
    if (toggles.bluedot && isStacked) layerManager.showBluedotHeatmap(analyticsClient);
    else layerManager.clearBluedot();
  }, [toggles.bluedot, mapView, mapData, analyticsClient, isStacked]);

  useEffect(() => {
    if (!mapView || !mapData) return;
    if (toggles.events && isStacked) layerManager.showEvents(mapEvents);
    else layerManager.clearEvents();
  }, [toggles.events, mapView, mapData, mapEvents, isStacked]);

  useEffect(() => {
    if (!mapView || !mapData) return;
    if (toggles.vacancy && isStacked) layerManager.showVacancy();
    else layerManager.clearVacancy();
  }, [toggles.vacancy, mapView, mapData, isStacked]);

  const hasElevators = useMemo(() => {
    if (!mapData) return false;
    try {
      const conns = (mapData.getByType('connection') as MappedinConnection[]) ?? [];
      for (const c of conns) {
        const ct = (c.type ?? c.connectionType ?? '').toLowerCase();
        const cn2 = (c.name ?? '').toLowerCase();
        if (
          ct.includes('elevator') ||
          ct.includes('lift') ||
          cn2.includes('elevator') ||
          cn2.includes('lift')
        ) {
          return true;
        }
      }
    } catch {
      // Ignore.
    }
    return false;
  }, [mapData]);

  useEffect(() => () => layerManager.clearAll(), []);

  if (!mapView) return null;

  const kc = directoryKiosks.length;
  const contracted = !isStacked;
  const leasingEnabled = false;
  const mob = typeof window !== 'undefined' ? window.innerWidth < 768 : false;

  const securityActive = toggles.cam || toggles.guard;
  const experienceActive = toggles.dir || toggles.bluedot || toggles.events;
  const leasingActive = toggles.vacancy;
  const sensorsActive =
    toggles.access || toggles.temp || toggles.assets || (toggles.elev && hasElevators);

  const sections: Section[] = [
    {
      slug: 'mappedin-security',
      icon: <Cctv className="w-4 h-4" />,
      name: 'Security',
      color: '#ef4444',
      active: securityActive,
      isApp: true,
      children: [
        {
          toggleKey: 'cam',
          label: 'Cameras',
          active: toggles.cam,
          toggle: (v) => setToggle('cam', v),
          icon: <Video className="w-3.5 h-3.5" />,
          color: '#f59e0b',
          dimmed: contracted,
        },
        {
          toggleKey: 'guard',
          label: 'Officers',
          active: toggles.guard,
          toggle: (v) => setToggle('guard', v),
          icon: <ShieldCheck className="w-3.5 h-3.5" />,
          color: '#22c55e',
          dimmed: contracted,
        },
      ],
    },
    {
      slug: 'experience',
      icon: <Sparkles className="w-4 h-4" />,
      name: 'Experience',
      color: '#6366f1',
      active: experienceActive,
      isApp: false,
      children: [
        {
          toggleKey: 'dir',
          label: kc ? `${kc} Directories` : 'Directories',
          active: toggles.dir,
          toggle: (v) => setToggle('dir', v),
          icon: <Monitor className="w-3.5 h-3.5" />,
          color: '#3b82f6',
          disabled: directoryKiosksLoading || kc === 0,
          dimmed: contracted,
        },
        {
          toggleKey: 'events',
          label: 'Events & Deals',
          active: toggles.events,
          toggle: (v) => setToggle('events', v),
          icon: <CalendarDays className="w-3.5 h-3.5" />,
          color: '#8b5cf6',
          dimmed: contracted,
        },
        {
          toggleKey: 'bluedot',
          label: 'BlueDot Hotspots',
          active: toggles.bluedot,
          toggle: (v) => setToggle('bluedot', v),
          icon: <Radar className="w-3.5 h-3.5" />,
          color: '#8b5cf6',
          dimmed: contracted,
        },
      ],
    },
    {
      slug: 'leasing',
      icon: <Building2 className="w-4 h-4" />,
      name: 'Leasing',
      color: '#f97316',
      active: leasingActive,
      isApp: false,
      disabled: !leasingEnabled,
      children: [
        {
          toggleKey: 'vacancy',
          label: 'Vacancies',
          active: toggles.vacancy,
          toggle: (v) => setToggle('vacancy', v),
          icon: <SquareDashedBottom className="w-3.5 h-3.5" />,
          color: '#f97316',
          dimmed: !isStacked,
          disabled: !leasingEnabled,
        },
      ],
    },
    {
      slug: 'sensors',
      icon: <Radio className="w-4 h-4" />,
      name: 'Sensors',
      color: '#14b8a6',
      active: sensorsActive,
      isApp: false,
      children: [
        {
          toggleKey: 'access',
          label: 'Access Control',
          active: toggles.access,
          toggle: (v) => setToggle('access', v),
          icon: <DoorOpen className="w-3.5 h-3.5" />,
          color: '#14b8a6',
          dimmed: contracted,
        },
        {
          toggleKey: 'temp',
          label: 'Temperature',
          active: toggles.temp,
          toggle: (v) => setToggle('temp', v),
          icon: <Thermometer className="w-3.5 h-3.5" />,
          color: '#14b8a6',
          dimmed: contracted,
        },
        {
          toggleKey: 'assets',
          label: 'Assets',
          active: toggles.assets,
          toggle: (v) => setToggle('assets', v),
          icon: <PackageCheck className="w-3.5 h-3.5" />,
          color: '#14b8a6',
          dimmed: contracted,
        },
        ...(hasElevators
          ? [
              {
                toggleKey: 'elev' as const,
                label: 'Elevators',
                active: toggles.elev,
                toggle: (v: boolean) => setToggle('elev', v),
                icon: <ArrowUpDown className="w-3.5 h-3.5" />,
                color: '#14b8a6',
                dimmed: contracted,
              },
            ]
          : []),
      ],
    },
  ];

  if (mob) return <NavMobile sections={sections} client={client} />;

  const navigateTo = (path: string): void => {
    try {
      client?.setRoute(path);
    } catch {
      // Ignore if host isn't routable.
    }
  };

  return (
    <div className="sidenav">
      <div className="sidenav-inner">
        {sections.map((sec) => {
          const hasKids = sec.children.length > 0;
          const appColor = sec.disabled ? '#475569' : sec.color;
          return (
            <div
              key={sec.slug}
              className={cn('sidenav-group', sec.disabled && 'sidenav-group-disabled')}
            >
              <div
                className="sidenav-section-bar"
                style={{
                  background: appColor,
                  opacity: sec.disabled ? 0.15 : undefined,
                }}
              />
              <button
                type="button"
                className={cn(
                  'sidenav-app-btn',
                  sec.active && 'sidenav-app-btn-active',
                  sec.disabled && 'sidenav-app-btn-disabled',
                )}
                onClick={
                  sec.isApp && !sec.disabled ? () => navigateTo(`/apps/${sec.slug}`) : undefined
                }
                title={sec.disabled ? `${sec.name} (not enabled)` : sec.name}
                style={
                  {
                    '--app-color': appColor,
                    cursor: sec.isApp && !sec.disabled ? 'pointer' : 'default',
                  } as React.CSSProperties
                }
              >
                <div
                  className="sidenav-app-icon"
                  style={{
                    color: sec.disabled ? '#64748b' : sec.color,
                    opacity: sec.disabled ? 0.5 : 1,
                  }}
                >
                  {sec.icon}
                </div>
                <span className="sidenav-app-name">{sec.name}</span>
                {sec.isApp && !sec.disabled && (
                  <ArrowUpRight className="w-3 h-3 sidenav-app-arrow" />
                )}
                {sec.disabled && <span className="sidenav-app-badge">Not enabled</span>}
              </button>

              {hasKids && (
                <div className="sidenav-children">
                  {sec.children.map((child) => (
                    <NavToggle key={child.toggleKey} {...child} />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <div className="sidenav-spacer" />

        <button
          type="button"
          className="sidenav-item sidenav-more"
          onClick={() => navigateTo('/marketplace')}
          title="More Apps"
        >
          <div className="sidenav-icon">
            <Plus className="w-4 h-4" />
          </div>
          <span className="sidenav-label">More Apps</span>
        </button>
      </div>
    </div>
  );
}

function NavToggle(props: ChildItem): React.JSX.Element {
  const handleClick = (): void => {
    if (props.disabled) return;
    props.toggle(!props.active);
  };
  const activeStyle =
    props.active && !props.disabled ? { background: `${props.color}aa` } : undefined;
  return (
    <button
      type="button"
      className={cn(
        'sidenav-toggle',
        props.active && 'sidenav-toggle-on',
        props.disabled && 'sidenav-toggle-disabled',
        props.dimmed && props.active && 'sidenav-toggle-dimmed',
      )}
      onClick={handleClick}
      title={props.label}
    >
      <div className="sidenav-toggle-icon-wrap">
        {props.icon}
        {props.active && (
          <span className="sidenav-toggle-check" style={{ background: props.color }}>
            <Check className="w-[6px] h-[6px]" strokeWidth={3} />
          </span>
        )}
      </div>
      <span className="sidenav-toggle-label">{props.label}</span>
      <span
        className={cn('sidenav-switch', props.active && 'sidenav-switch-on')}
        style={activeStyle}
      >
        <span className="sidenav-switch-thumb" />
      </span>
    </button>
  );
}

interface NavMobileProps {
  sections: Section[];
  client: VenueOSClient | null;
}

function NavMobile({ sections, client }: NavMobileProps): React.JSX.Element {
  const navigateTo = (path: string): void => {
    try {
      client?.setRoute(path);
    } catch {
      // Ignore.
    }
  };

  return (
    <div className="mobilenav">
      <button
        type="button"
        className="mobilenav-item"
        onClick={() => useAppStore.getState().backToGlobe()}
      >
        <GlobeIcon className="w-5 h-5" />
      </button>
      {sections.map((sec) => (
        <Fragment key={sec.slug}>
          <button
            type="button"
            className={cn('mobilenav-item', sec.active && 'mobilenav-item-active')}
            style={sec.active ? { color: sec.color } : undefined}
            onClick={sec.isApp ? () => navigateTo(`/apps/${sec.slug}`) : undefined}
          >
            {sec.icon}
            {sec.active && <span className="mobilenav-dot" style={{ background: sec.color }} />}
          </button>
          {sec.children.map((child) => (
            <button
              type="button"
              key={child.toggleKey}
              className={cn(
                'mobilenav-item mobilenav-toggle',
                child.active && 'mobilenav-item-active',
              )}
              style={child.active ? { color: child.color } : undefined}
              onClick={child.disabled ? undefined : () => child.toggle(!child.active)}
            >
              <div className="mobilenav-icon-wrap">
                {child.icon}
                {child.active && (
                  <span className="mobilenav-check" style={{ background: child.color }}>
                    <Check className="w-[5px] h-[5px]" strokeWidth={3} />
                  </span>
                )}
              </div>
            </button>
          ))}
        </Fragment>
      ))}
      <button type="button" className="mobilenav-item" onClick={() => navigateTo('/marketplace')}>
        <Plus className="w-5 h-5" style={{ opacity: 0.4 }} />
      </button>
    </div>
  );
}
